import { supabase } from "./supabaseClient";

// Nilai default — dipakai kalau fetch gagal (offline, dll) ATAU baris
// setting tertentu belum ada di database, supaya aplikasi tidak pernah
// tampil rusak/kosong karena tema tidak ketemu.
export const DEFAULT_THEME = {
  app_name: "MLV Teknologi",
  app_subtitle: "RoomDesigner 3D",
  theme_preset: "light", // "light" | "dark" — sumber kebenaran utama
  theme_bg: "#f1f5f9",
  theme_surface: "#ffffff",
  theme_text: "#0f172a",
  theme_text_muted: "#94a3b8",
  theme_border: "#e2e8f0",
  theme_accent: "#3b82f6",
  theme_accent_text: "#ffffff",
  footer_text: "© {year} MLV Teknologi RoomDesigner 3D",
  // ── Model 3D custom untuk furniture bawaan (meja & kursi) — kosong
  // berarti pakai geometri three.js bawaan (box meja + kursi kantor).
  // Disimpan di tabel app_settings yang sama dengan tema (key-value),
  // tidak perlu tabel/kolom baru. Diisi lewat halaman admin Pengaturan
  // Tampilan, memakai komponen upload .glb yang sama dengan produk.
  table_model_url: "",
  table_model_scale: "1",
  table_model_rotation_y: "0",
  chair_model_url: "",
  chair_model_scale: "1",
  chair_model_rotation_y: "0",
};

// Dua preset tema siap pakai — Light (tema terang saat ini) dan Dark
// (tema gelap navy yang dulu pernah dipakai). User cukup pilih salah satu
// dari halaman admin, tanpa perlu mengatur warna satu-satu secara manual.
export const THEME_PRESETS = {
  light: {
    label: "Terang",
    theme_bg: "#f1f5f9",
    theme_surface: "#ffffff",
    theme_text: "#0f172a",
    theme_text_muted: "#94a3b8",
    theme_border: "#e2e8f0",
    theme_accent: "#3b82f6",
    theme_accent_text: "#ffffff",
  },
  dark: {
    label: "Gelap",
    theme_bg: "#0f172a",
    theme_surface: "#1e293b",
    theme_text: "#f1f5f9",
    theme_text_muted: "#94a3b8",
    theme_border: "rgba(255,255,255,0.08)",
    theme_accent: "#3b82f6",
    theme_accent_text: "#ffffff",
  },
};

// Warna kategori default — HARUS identik dengan CATEGORY_COVERAGE_COLORS
// yang sudah ada di RoomDesigner.js, supaya fallback offline tetap
// konsisten dengan geometri 3D yang sudah dirancang.
export const DEFAULT_CATEGORY_COLORS = {
  Display: "#a78bfa",
  Conference: "#3b82f6",
  Microphone: "#22c55e",
  Speaker: "#f59e0b",
};

/**
 * Ambil tema UI dari tabel app_settings. Mengembalikan { theme, error }.
 * theme selalu lengkap (di-merge dengan DEFAULT_THEME untuk key yang
 * belum ada di database), tidak pernah undefined per-field.
 */
export async function fetchTheme() {
  const { data, error } = await supabase.from("app_settings").select("key, value");
  if (error) {
    return { theme: DEFAULT_THEME, error };
  }
  const theme = { ...DEFAULT_THEME };
  for (const row of data || []) {
    theme[row.key] = row.value;
  }
  return { theme, error: null };
}

/**
 * Ambil warna kategori dari tabel categories. Mengembalikan
 * { categoryColors, error } — object { CategoryId: "#hex" }.
 */
export async function fetchCategoryColors() {
  const { data, error } = await supabase.from("categories").select("id, coverage_color");
  if (error) {
    return { categoryColors: DEFAULT_CATEGORY_COLORS, error };
  }
  const categoryColors = { ...DEFAULT_CATEGORY_COLORS };
  for (const row of data || []) {
    categoryColors[row.id] = row.coverage_color;
  }
  return { categoryColors, error: null };
}

/**
 * Simpan satu atau lebih key tema (dipakai dari halaman admin settings).
 * entries: { key: value, ... }
 */
export async function saveThemeSettings(entries) {
  const rows = Object.entries(entries).map(([key, value]) => ({ key, value: String(value) }));
  const { error } = await supabase.from("app_settings").upsert(rows, { onConflict: "key" });
  return { error };
}

/**
 * Update warna kategori (coverage_color) untuk satu kategori.
 */
export async function saveCategoryColor(categoryId, color) {
  const { error } = await supabase
    .from("categories")
    .update({ coverage_color: color })
    .eq("id", categoryId);
  return { error };
}